import { nanoid } from 'nanoid';
import { Model } from '../model/Model';
import { RuntimeScheduler } from './modules/scheduler/RuntimeScheduler';
import { memoize } from '../../utils/mobx/decorators/memoize';
import { RuntimeClock } from './modules/clock/RuntimeClock';
import { RuntimeInjected } from './inject/RuntimeInjected';
import { ServerRuntime } from '../../server/serverRuntime/ServerRuntime';
import { AvailablePackages } from './package/AvailablePackages';
import { StreamedPackages } from './package/StreamedPackages';
import { RuntimePackages } from './modules/RuntimePackages';
import { BrowserRuntime } from './browser/BrowserRuntime';
import { RuntimeMobx } from './modules/RuntimeMobx';
import { SolidPackages } from './package/SolidPackages';
import { FetchPackages } from './package/FetchPackages';
import { RuntimePermissions } from './modules/RuntimePermissions';
import { RuntimeInfo } from './modules/RuntimeInfo';
import { RuntimeSync } from './modules/RuntimeSync';
import { RegistryMetaInfo } from '../../packages/Edit/registryMeta/RegistryMetaInfo';
import { Registry } from '../../registry/Registry';
import { Devices } from '../device/Devices';
import { Keyboard } from '../device/keyboard/Keyboard';
import { RuntimeHostname } from './modules/RuntimeHostname';
import { RuntimeTimeSwitch } from './modules/timeSwitch/RuntimeTimeSwitch';
import { Persistent } from '../api/Persistent';
import { BrowserBot } from '../../packages/EditLazy/classes/bot/BrowserBot';
import { ax, specificAxios } from './axios/axios';

export class Runtime {
  readonly type = 'runtime';
  serverRuntime: ServerRuntime | undefined;
  browserRuntime: BrowserRuntime | undefined;
  readonly isServerRuntime: boolean = typeof window === 'undefined';
  readonly isBrowserRuntime: boolean = typeof window !== 'undefined';

  runtimeInjected: RuntimeInjected;
  registry: Registry;



  @memoize
  get id(): string {
    return nanoid(8);
  }

  @memoize
  get axios() {
    if (this.runtimePermissions.hasEditFunctionality) {
      return specificAxios({ headers: { 'Cache-Control': 'no-cache' } });
    }
    return ax;
  }

  @memoize
  get persistent() {
    return new Persistent(this);
  }

  @memoize
  get runtimeMobx() {
    return new RuntimeMobx(this);
  }

  @memoize
  get runtimePackages() {
    return new RuntimePackages(this);
  }

  @memoize
  get runtimeClock() {
    return new RuntimeClock(this);
  }

  @memoize
  get runtimeTimeSwitch() {
    return new RuntimeTimeSwitch(this);
  }

  @memoize
  get runtimeInfo() {
    return new RuntimeInfo(this);
  }

  @memoize
  get runtimeSync() {
    return new RuntimeSync(this);
  }

  @memoize
  get runtimePermissions() {
    return new RuntimePermissions(this);
  }

  @memoize
  get runtimeScheduler() {
    return new RuntimeScheduler(this);
  }

  @memoize
  get runtimeHostname() {
    return new RuntimeHostname(this);
  }

  get isBot(): boolean {
    return !!this.browserRuntime && this.browserRuntime.browserAddressBar.query.bot === '1';
  }

  @memoize
  get runtimeBot() {
    return new BrowserBot(this);
  }

  @memoize
  get devices() {
    return new Devices(this);
  }

  get class() {
    return this.runtimeInjected.entityClass;
  }

  get solidServerRuntime(): ServerRuntime {
    return this.serverRuntime as ServerRuntime;
  }

  get solidBrowserRuntime(): BrowserRuntime {
    return this.browserRuntime as BrowserRuntime;
  }

  @memoize
  get model(): Model {
    return new this.class.Model(this);
  }

  get availablePackages(): AvailablePackages {
    return this.runtimePackages.packageShelf.availablePackages;
  }

  get streamedPackages(): StreamedPackages {
    return this.runtimePackages.packageShelf.streamedPackages;
  }

  get solidPackages(): SolidPackages {
    return this.runtimePackages.packageShelf.solidPackages;
  }

  get fetchPackages(): FetchPackages {
    return this.runtimePackages.packageShelf.fetchedPackages;
  }

  get keyboard(): Keyboard {
    return this.devices.keyboard;
  }

  get editChunks(): RegistryMetaInfo {
    return this.solidPackages.Edit.registryMetaInfo;
  }

  get isDevelopmentStage(): boolean {
    if (this.isServerRuntime) {
      return this.solidServerRuntime.envService.isDevelopmentStage;
    }
      return this.solidBrowserRuntime.browserStaging.isDevHostname;
  }

  get isQualityStage(): boolean {
    if (this.isServerRuntime) {
      return this.solidServerRuntime.envService.isQualityStage;
    }
    return this.solidBrowserRuntime.browserStaging.isQsHostname;
  }

  get isProductionStage(): boolean {
    return !this.isDevelopmentStage && !this.isQualityStage;
  }

  get stagePrefix(): 'dev' | 'qs' | 'www' {
    if (this.isDevelopmentStage) return 'dev';
    if (this.isQualityStage) return 'dev';
    return 'www';
  }

  get dbPrefix(): 'dev_' | '' {
    if (this.isDevelopmentStage) return 'dev_';
    return '';
  }
}

export const runtime = new Runtime();
